
import React, { useState, useEffect, useMemo } from 'react';
import { 
  TrendingUp, Clock, Zap, Calendar, 
  BarChart3, PieChart,
  FileText, Languages,
  ArrowUpRight, ArrowDownRight,
  MessageSquare, Star, Bell, Send,
  Mail, CheckCircle2, ShieldAlert, Brain, X,
  HelpCircle, DollarSign
} from 'lucide-react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, AreaChart, Area, Cell, PieChart as RePieChart, Pie,
  LineChart, Line
} from 'recharts';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, legacyDb, getDefaultDb } from '../firebaseConfig';
import { 
  fetchUserRealStats, 
  saveAppSuggestion, 
  saveModuleRating, 
  subscribeToAppNotices,
  subscribeToUserMessages,
  markUserMessageAsRead,
  subscribeToActiveChats,
  subscribeToDirectory,
  subscribeToUserProfile,
  subscribeToSystemPricing
} from '../services/db';
import { migrateUserDataFromUS, dismissMigration } from '../services/migrationService';
import { ROIEventType, AppNotice, UserMessage, PeerChat, UserProfile, ViewState } from '../types';
import { formatHours } from '../utils/format';
import { collapseGenerationEventsBySession } from '../utils/sessionStats';

interface DashboardUserScreenProps {
  isVerified?: boolean;
  isProfileIncomplete?: boolean;
  onNavigate?: (view: any) => void;
}

const DashboardUserScreen: React.FC<DashboardUserScreenProps> = ({ 
  isVerified = false, 
  isProfileIncomplete = false,
  onNavigate
}) => {
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('app_theme') === 'dark');
  const [timeRange, setTimeRange] = useState<'today' | 'day' | 'month' | 'year'>('day');
  const [statsPeriod, setStatsPeriod] = useState<'day' | 'week' | 'month' | 'year'>('week');
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [suggestion, setSuggestion] = useState('');
  const [isSendingSuggestion, setIsSendingSuggestion] = useState(false);
  const [notices, setNotices] = useState<AppNotice[]>([]);
  const [userMessages, setUserMessages] = useState<UserMessage[]>([]);
  const [activeChats, setActiveChats] = useState<PeerChat[]>([]);
  const [directory, setDirectory] = useState<UserProfile[]>([]);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [personalDbStats, setPersonalDbStats] = useState<{
    reports: number,
    phrases: number,
    categories?: number
  } | null>(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [showBridge, setShowBridge] = useState(false);
  const [isBridgeDismissed, setIsBridgeDismissed] = useState(false);
  const [isBridgeExpanded, setIsBridgeExpanded] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [monthTokenCost, setMonthTokenCost] = useState<number>(0);
  const [systemBaseFeeDefault, setSystemBaseFeeDefault] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('radia_dynamic_pricing');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.baseFeeDefault !== undefined) return parsed.baseFeeDefault;
      }
    } catch {
      // Usar valor por defecto si no existe o falla parse
    }
    return 30;
  });

  useEffect(() => {
    if (!auth.currentUser || auth.currentUser.uid === 'guest_dev_offline') return;

    const unsubProfile = subscribeToUserProfile(auth.currentUser.uid, (profile) => {
      if (profile) {
        setUserProfile(profile);
      }
    });

    const unsubPricing = subscribeToSystemPricing((pricing) => {
      if (pricing && pricing.baseFeeDefault !== undefined) {
        setSystemBaseFeeDefault(pricing.baseFeeDefault);
      }
    });

    const loadMonthTokenCost = async () => {
      try {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const res = await fetchUserRealStats(auth.currentUser!.uid, startOfMonth);
        if (res && res.tokens) {
          setMonthTokenCost(res.tokens.totalCost);
        }
      } catch (e) {
        console.error("Error cargando consumo de tokens del mes actual:", e);
      }
    };
    loadMonthTokenCost();

    return () => {
      unsubProfile();
      unsubPricing();
    };
  }, []);

  const currentBaseFee = userProfile?.customBaseFee !== undefined && userProfile.customBaseFee !== null
    ? (userProfile.customBaseFee as number)
    : systemBaseFeeDefault;
  const isCustomFee = userProfile?.customBaseFee !== undefined && userProfile.customBaseFee !== null;
  const totalMonthCost = currentBaseFee + monthTokenCost;

  const checkPersonalDataBridge = async () => {
    if (!auth.currentUser) return;
    try {
      const { collection: coll, getDocs: getD, query: qry, limit: lmt, doc: dRef, getDoc: gDoc } = await import('firebase/firestore');
      
      const userId = auth.currentUser.uid;
      
      // First check EU profile for status
      const profileSnap = await gDoc(dRef(db, "users", userId, "profile", "main"));
      const profileData = profileSnap.data() as UserProfile;
      setUserProfile(profileData);

      if (profileData?.dataRescueStatus === 'COMPLETED' || profileData?.dataRescueStatus === 'DISMISSED') {
        setShowBridge(false);
        return;
      }

      // Force show to ensure accessibility if not completed
      setShowBridge(true);
      
      // Light check for stats (silent finish)
      try {
        const legacyReports = await getD(qry(coll(legacyDb, "users", userId, "reports"), lmt(1)));
        const legacyPhrases = await getD(qry(coll(legacyDb, "users", userId, "phrases"), lmt(1)));
        
        setPersonalDbStats({
          reports: legacyReports.size > 0 ? 99 : 0,
          phrases: legacyPhrases.size > 0 ? 99 : 0
        });
      } catch (e) {
        console.warn("Legacy data scan failed, but keeping bridge visible:", e);
      }
    } catch (error) {
      console.warn("Personal Data Bridge check failed:", error);
    }
  };

  useEffect(() => {
    const loadStats = async () => {
      if (!auth.currentUser) return;
      
      // Cache de sesión para evitar lecturas redundantes al navegar (Ahorro de Cuota)
      const CACHE_KEY = `radia_stats_${auth.currentUser.uid}`;
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        setStats(JSON.parse(cached));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const userId = auth.currentUser.uid;
        const data = await fetchUserRealStats(userId);
        setStats(data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
      } catch (error) {
        console.error("Error loading user stats:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Suscribirse a avisos
    const unsubscribeNotices = subscribeToAppNotices((data) => {
      setNotices(data);
    });

    // Suscribirse a mensajes personales y chats
    let unsubscribeMessages = () => {};
    let unsubscribeChats = () => {};
    let unsubscribeDir = () => {};

    if (auth.currentUser) {
      loadStats();
      checkPersonalDataBridge();
      unsubscribeMessages = subscribeToUserMessages(auth.currentUser.uid, (data) => {
        setUserMessages(data);
      });
      unsubscribeChats = subscribeToActiveChats(auth.currentUser.uid, (data) => {
        setActiveChats(data);
      });
      unsubscribeDir = subscribeToDirectory((data) => {
        setDirectory(data);
      });
    }

    return () => {
      unsubscribeNotices();
      unsubscribeMessages();
      unsubscribeChats();
      unsubscribeDir();
    };
  }, []);

  const handleMarkAsRead = async (msgId: string) => {
    if (!auth.currentUser) return;
    await markUserMessageAsRead(auth.currentUser.uid, msgId);
  };

  const handleOpenChat = (chat: PeerChat) => {
    if (!auth.currentUser) return;
    const otherId = chat.participants.find(p => p !== auth.currentUser?.uid);
    if (otherId) {
      localStorage.setItem('modular_open_chat_user_id', otherId);
      onNavigate?.(ViewState.COMMUNITY);
    }
  };

  useEffect(() => {
    const handleThemeChange = () => {
      setIsDarkMode(localStorage.getItem('app_theme') === 'dark');
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  const roiData = useMemo(() => {
    if (!stats) return [];
    
    let source: { label: string, hours: number }[] = [];
    if (timeRange === 'today') source = stats.hourlyROI || [];
    else if (timeRange === 'day') source = stats.dailyROI || [];
    else if (timeRange === 'month') source = stats.monthlyROI || [];
    else source = stats.yearlyROI || [];

    return source.map(d => ({
      name: d.label,
      value: Number(d.hours.toFixed(2))
    }));
  }, [stats, timeRange]);

  const periodStats = useMemo(() => {
    const sessions = collapseGenerationEventsBySession(stats?.roiEvents || []);
    const now = new Date();

    const getBounds = (period: 'day' | 'week' | 'month' | 'year', offset: number) => {
      if (period === 'day') {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
        return { start: d.getTime(), end: d.getTime() + 24 * 60 * 60 * 1000 - 1 };
      }
      if (period === 'week') {
        const dayOfWeek = now.getDay();
        const startOfThisWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
        const start = new Date(startOfThisWeek.getFullYear(), startOfThisWeek.getMonth(), startOfThisWeek.getDate() + offset * 7);
        return { start: start.getTime(), end: start.getTime() + 7 * 24 * 60 * 60 * 1000 - 1 };
      }
      if (period === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0, 23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      // year
      const start = new Date(now.getFullYear() + offset, 0, 1);
      const end = new Date(now.getFullYear() + offset, 11, 31, 23, 59, 59, 999);
      return { start: start.getTime(), end: end.getTime() };
    };

    const compute = (period: 'day' | 'week' | 'month' | 'year', offset: number) => {
      const { start, end } = getBounds(period, offset);
      const inRange = sessions.filter(s => s.timestamp >= start && s.timestamp <= end);
      if (inRange.length === 0) return { count: 0, avgTotalMinutes: 0 };
      const totalSeconds = inRange.reduce((sum, s) => sum + s.dictationSeconds + s.fusionSeconds, 0);
      return { count: inRange.length, avgTotalMinutes: (totalSeconds / inRange.length) / 60 };
    };

    const result: Record<'day' | 'week' | 'month' | 'year', { current: { count: number; avgTotalMinutes: number }, previous: { count: number; avgTotalMinutes: number } }> = {} as any;
    (['day', 'week', 'month', 'year'] as const).forEach(period => {
      result[period] = { current: compute(period, 0), previous: compute(period, -1) };
    });
    return result;
  }, [stats]);

  const reportsTrendData = useMemo(() => {
    const sessions = collapseGenerationEventsBySession(stats?.roiEvents || []);
    const now = new Date();
    const points: { name: string; informes: number }[] = [];

    if (statsPeriod === 'day') {
      // Últimos 30 días del mes actual (día 1 al último día del mes)
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      for (let d = 1; d <= lastDay; d++) {
        const dayStart = new Date(now.getFullYear(), now.getMonth(), d).getTime();
        const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
        const count = sessions.filter(s => s.timestamp >= dayStart && s.timestamp <= dayEnd).length;
        points.push({ name: d.toString().padStart(2, '0'), informes: count });
      }
    } else if (statsPeriod === 'week') {
      // Últimas 8 semanas
      for (let i = 7; i >= 0; i--) {
        const dayOfWeek = now.getDay();
        const startOfThisWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
        const start = new Date(startOfThisWeek.getFullYear(), startOfThisWeek.getMonth(), startOfThisWeek.getDate() - i * 7);
        const end = start.getTime() + 7 * 24 * 60 * 60 * 1000 - 1;
        const count = sessions.filter(s => s.timestamp >= start.getTime() && s.timestamp <= end).length;
        points.push({ name: `${start.getDate()}/${start.getMonth() + 1}`, informes: count });
      }
    } else if (statsPeriod === 'month') {
      // Meses del año actual
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      for (let m = 0; m < 12; m++) {
        const start = new Date(now.getFullYear(), m, 1).getTime();
        const end = new Date(now.getFullYear(), m + 1, 0, 23, 59, 59, 999).getTime();
        const count = sessions.filter(s => s.timestamp >= start && s.timestamp <= end).length;
        points.push({ name: monthNames[m], informes: count });
      }
    } else {
      // Últimos 5 años
      for (let y = now.getFullYear() - 4; y <= now.getFullYear(); y++) {
        const start = new Date(y, 0, 1).getTime();
        const end = new Date(y, 11, 31, 23, 59, 59, 999).getTime();
        const count = sessions.filter(s => s.timestamp >= start && s.timestamp <= end).length;
        points.push({ name: y.toString(), informes: count });
      }
    }
    return points;
  }, [stats, statsPeriod]);

  const interactionData = useMemo(() => {
    if (!stats?.roiEvents) return [];
    
    const knownGroups: { name: string; types: ROIEventType[]; color: string }[] = [
      { name: 'Generación', types: [ROIEventType.GENERATION, ROIEventType.GENERATE_RX, ROIEventType.GENERATE_TC, ROIEventType.GENERATE_RM, ROIEventType.AGENT_REPORT], color: '#3b82f6' },
      { name: 'Traducción', types: [ROIEventType.TRANSLATION], color: '#10b981' },
      { name: 'Mejora/Auditoría', types: [ROIEventType.IMPROVEMENT, ROIEventType.AUDIT, ROIEventType.PHD_AUDIT, ROIEventType.RECONCILIATION], color: '#f59e0b' },
      { name: 'Asistencia PHD', types: [ROIEventType.PHD_ASSISTANCE], color: '#f97316' },
      { name: 'Estructuración', types: [ROIEventType.STRUCTURE, ROIEventType.NARRATIVE, ROIEventType.OPTIMIZE], color: '#8b5cf6' },
      { name: 'Transcripción', types: [ROIEventType.TRANSCRIPTION], color: '#06b6d4' },
      { name: 'Otros', types: [ROIEventType.CONCLUSION, ROIEventType.REFINE, ROIEventType.SUMMARIZE, ROIEventType.SAVE_REPORT], color: '#94a3b8' },
    ];

    const counts: Record<string, number> = {};
    knownGroups.forEach(g => { counts[g.name] = 0; });

    stats.roiEvents.forEach((event: any) => {
      const group = knownGroups.find(g => g.types.includes(event.type));
      if (group) {
        counts[group.name]++;
      } else {
        counts['Otros'] = (counts['Otros'] || 0) + 1;
      }
    });

    return knownGroups
      .map(g => ({ name: g.name, value: counts[g.name], color: g.color }))
      .filter(item => item.value > 0);
  }, [stats]);

  const monthlyTimingData = useMemo(() => {
    if (!stats?.roiEvents) return [];

    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const groups: Record<string, { dictation: number[]; fusion: number[]; timestamp: number; label: string }> = {};

    stats.roiEvents.forEach((event: any) => {
      if (
        event.type === ROIEventType.GENERATION &&
        event.metadata?.dictationSeconds !== undefined &&
        event.metadata?.fusionSeconds !== undefined
      ) {
        const d = new Date(event.timestamp);
        const year = d.getFullYear();
        const month = d.getMonth();
        const key = `${year}-${String(month + 1).padStart(2, '0')}`;
        const label = `${monthNames[month]} ${year}`;

        if (!groups[key]) {
          groups[key] = {
            dictation: [],
            fusion: [],
            timestamp: new Date(year, month, 1).getTime(),
            label
          };
        }
        groups[key].dictation.push(Number(event.metadata.dictationSeconds));
        groups[key].fusion.push(Number(event.metadata.fusionSeconds));
      }
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => groups[a].timestamp - groups[b].timestamp);

    return sortedKeys.map(key => {
      const g = groups[key];
      const count = g.dictation.length;
      const avgDictMin = Number(((g.dictation.reduce((sum, val) => sum + val, 0) / count) / 60).toFixed(2));
      const avgFusionMin = Number(((g.fusion.reduce((sum, val) => sum + val, 0) / count) / 60).toFixed(2));
      const avgTotalMin = Number((avgDictMin + avgFusionMin).toFixed(2));

      return {
        name: g.label,
        dictado: avgDictMin,
        fusion: avgFusionMin,
        total: avgTotalMin,
        count
      };
    });
  }, [stats]);

  const handleSendSuggestion = async () => {
    if (!suggestion.trim() || !auth.currentUser) return;
    setIsSendingSuggestion(true);
    try {
      await saveAppSuggestion({
        userId: auth.currentUser.uid,
        userName: auth.currentUser.displayName || 'Usuario',
        text: suggestion,
        timestamp: Date.now(),
        status: 'pending'
      });
      setSuggestion('');
      alert('Gracias por su colaboración. Su sugerencia ha sido registrada para análisis.');
    } catch (error) {
      console.error("Error sending suggestion:", error);
    } finally {
      setIsSendingSuggestion(false);
    }
  };

  const handleRateModule = async (moduleId: string, rating: number) => {
    if (!auth.currentUser) return;
    try {
      await saveModuleRating({
        userId: auth.currentUser.uid,
        moduleId,
        rating,
        timestamp: Date.now()
      });
      setRatings(prev => ({ ...prev, [moduleId]: rating }));
    } catch (error) {
      console.error("Error saving rating:", error);
    }
  };

  const migratePersonalData = async () => {
    if (!auth.currentUser) {
      alert("No se ha detectado una sesión activa. Por favor, inicie sesión de nuevo.");
      return;
    }
    
    const userConfirmed = confirm("Se han localizado informes y frases en la base de datos de USA. ¿Desea rescatar estos archivos y traerlos a su biblioteca actual en Europa?\n\nEste proceso puede tardar unos segundos.");
    
    if (!userConfirmed) return;
    
    setIsMigrating(true);
    try {
      console.log("[Dashboard] Initializing data rescue...");
      const result = await migrateUserDataFromUS(auth.currentUser.uid);
      
      if (result.error) {
        alert(`Atención durante el rescate:\n\n${result.error}`);
        if (result.reportsMigrated === 0 && result.phrasesMigrated === 0) {
           setIsMigrating(false);
           return;
        }
      }

      alert(`¡Rescate finalizado con éxito!\n\n- Informes recuperados: ${result.reportsMigrated}\n- Frases recuperadas: ${result.phrasesMigrated}\n- Categorías: ${result.categoriesMigrated}\n\nLa aplicación se reiniciará para cargar su nueva biblioteca.`);
      setShowBridge(false);
      window.location.reload();
    } catch (error: any) {
      console.error("Migration error in UI:", error);
      alert(`Error crítico en el proceso de rescate:\n${error?.message || "No se pudo conectar con el servicio de migración"}`);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleDismissBridge = async () => {
    if (!auth.currentUser) return;
    await dismissMigration(auth.currentUser.uid);
    setIsBridgeDismissed(true);
    setShowBridge(false);
  };

  if (isLoading) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-sm font-black uppercase tracking-widest text-blue-500">Cargando Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col overflow-y-auto custom-scrollbar transition-colors duration-300 ${isDarkMode ? 'bg-[#1e2024] text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
      <div className={`px-8 py-6 border-b sticky top-0 z-20 backdrop-blur-md ${isDarkMode ? 'bg-[#2a2d34]/80 border-gray-700' : 'bg-white/80 border-gray-200'}`}>
        <div className="flex justify-between items-center max-w-7xl mx-auto w-full">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-600 p-2.5 rounded-2xl shadow-lg shadow-blue-600/20">
              <TrendingUp size={24} className="text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-black tracking-tight uppercase ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Dashboard
              </h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
                Dashboard Personal de Productividad
              </p>
            </div>
          </div>

          <div className={`flex p-1 rounded-xl border ${isDarkMode ? 'bg-black/20 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
            {(['today', 'day', 'month', 'year'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${timeRange === range ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}
              >
                {range === 'today' ? 'Hoy' : range === 'day' ? 'Diario' : range === 'month' ? 'Meses' : 'Anual'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-8 max-w-7xl mx-auto w-full space-y-8">
        {/* Banner de Validación para Usuarios No Validados */}
        {(!isVerified || isProfileIncomplete) && (
          <div className={`p-8 rounded-[32px] border-2 border-dashed transition-all animate-in fade-in slide-in-from-top-4 duration-700 ${isDarkMode ? 'bg-amber-900/10 border-amber-500/30' : 'bg-amber-50 border-amber-200 shadow-xl shadow-amber-500/10'}`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-6">
                <div className={`w-20 h-20 rounded-3xl flex items-center justify-center shrink-0 shadow-2xl ${isDarkMode ? 'bg-amber-500/20 text-amber-400 shadow-amber-900/40' : 'bg-amber-100 text-amber-600 shadow-amber-200/50'}`}>
                  <ShieldAlert size={40} />
                </div>
                <div className="flex flex-col">
                  <h3 className={`text-2xl font-black uppercase tracking-tight ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                    Valida tu usuario para desbloquear MODULAR
                  </h3>
                  <p className={`text-sm font-medium leading-relaxed max-w-2xl mt-2 ${isDarkMode ? 'text-amber-400/80' : 'text-amber-600'}`}>
                    Tu perfil profesional aún no ha sido validado por nuestro sistema. Completa todos los campos obligatorios (*) en tu perfil para que nuestra IA verifique tu identidad y puedas acceder a todas las funciones avanzadas de la plataforma.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => onNavigate && onNavigate('PROFILE')}
                className={`px-10 py-4 rounded-2xl text-sm font-black uppercase tracking-widest transition-all shadow-2xl active:scale-95 whitespace-nowrap hover:-translate-y-1 ${isDarkMode ? 'bg-amber-600 text-white hover:bg-amber-500 shadow-amber-900/40' : 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-500/30'}`}
              >
                Validar mi Perfil ahora
              </button>
            </div>
          </div>
        )}

        {/* RADIA Data Rescue: Recuperación de Archivos de USA */}
        {showBridge && !isBridgeDismissed && (
          <div className={`p-8 rounded-[32px] border animate-in fade-in slide-in-from-top-4 duration-700 relative ${isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-100 shadow-lg shadow-indigo-500/10'}`}>
            <div className="absolute top-6 right-6 flex items-center gap-2">
              <button 
                onClick={handleDismissBridge}
                className="p-2 text-slate-400 hover:text-red-500 transition-colors flex items-center gap-2"
                title="No volver a mostrar"
              >
                <span className="text-[9px] font-black uppercase tracking-widest hidden sm:block">No volver a mostrar</span>
                <X size={16} />
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-6">
                <div className="bg-indigo-600 p-4 rounded-2xl shadow-xl shadow-indigo-600/20 animate-bounce-slow">
                  <Zap size={32} className="text-white" />
                </div>
                <div>
                  <h2 className={`text-xl font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Rescate de Datos: USA → Europa
                  </h2>
                  <p className="text-xs font-bold text-indigo-500 uppercase tracking-widest mt-1">
                    Hemos detectado informes y frases en la base de datos de USA.
                  </p>
                  <p className={`text-[11px] mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Para cumplir con la normativa Europea, sus datos deben ser migrados a esta región.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button 
                  onClick={handleDismissBridge}
                  className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${isDarkMode ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-white text-slate-400 hover:bg-slate-50 border border-slate-100'}`}
                >
                  Omitir por ahora
                </button>
                <button 
                  onClick={migratePersonalData}
                  disabled={isMigrating}
                  className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-600/20 flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  {isMigrating ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      <span>Rescatando datos...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Rescatar mis datos de USA</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <p className="mt-4 text-[9px] font-bold text-slate-400 uppercase tracking-widest text-center md:text-left">
              * Este proceso mueve sus informes y frases de la base de datos USA a la biblioteca Europea actual.
            </p>
          </div>
        )}

        {/* Row 1: 3-Column Grid (Eficiencia, Informes Completados, Tiempo Promedio por Informe) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Eficiencia Section with Period Tabs */}
          <div className={`rounded-[32px] border p-6 shadow-sm flex flex-col justify-between ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div>
              <div className="flex items-center justify-between gap-2 mb-6">
                <div className="flex items-center gap-2">
                  <TrendingUp size={18} className="text-indigo-500 shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs sm:text-sm font-black uppercase tracking-widest text-indigo-500">Eficiencia</h3>
                    <div className="relative group inline-flex items-center">
                      <HelpCircle size={14} className="text-slate-400 hover:text-indigo-500 cursor-help transition-colors" />
                      <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 w-56 p-2.5 rounded-xl shadow-xl border text-[11px] font-medium leading-relaxed pointer-events-none bg-slate-900 text-slate-100 border-slate-700">
                        Tiempo promedio vs. período anterior. Compara el tiempo invertido por estudio con el ciclo previo para medir la ganancia de velocidad.
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-0.5 p-0.5 rounded-lg border shrink-0 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                  {(['day', 'week', 'month', 'year'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setStatsPeriod(p)}
                      className={`px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-wider transition-all ${statsPeriod === p ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      {p === 'day' ? 'Día' : p === 'week' ? 'Sem' : p === 'month' ? 'Mes' : 'Año'}
                    </button>
                  ))}
                </div>
              </div>

              {(() => {
                const { current, previous } = periodStats[statsPeriod];
                if (current.count === 0) {
                  return (
                    <div className="py-12 text-center opacity-40">
                      <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">Sin informes en este período todavía</p>
                    </div>
                  );
                }
                const hasComparison = previous.count > 0;
                const pctChange = hasComparison ? ((previous.avgTotalMinutes - current.avgTotalMinutes) / previous.avgTotalMinutes) * 100 : 0;
                const isImproving = pctChange > 0;
                return (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {current.avgTotalMinutes.toFixed(1)} min
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">promedio por informe</span>
                    </div>
                    {hasComparison && (
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black ${isImproving ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                        {isImproving ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        <span>{Math.abs(pctChange).toFixed(1)}% {isImproving ? 'más rápido' : 'más lento'} que el período anterior</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Informes Completados Section */}
          <div className={`rounded-[32px] border p-6 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-1.5">
                <h3 className="text-xs sm:text-sm font-black uppercase tracking-widest text-blue-500">Informes Completados</h3>
                <div className="relative group inline-flex items-center">
                  <HelpCircle size={14} className="text-slate-400 hover:text-blue-500 cursor-help transition-colors" />
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 w-60 p-2.5 rounded-xl shadow-xl border text-[11px] font-medium leading-relaxed pointer-events-none bg-slate-900 text-slate-100 border-slate-700">
                    Cantidad de estudios finalizados en el período. Si un informe se regenera en la misma sesión, se computa una sola vez.
                  </div>
                </div>
              </div>
            </div>
            {reportsTrendData.every(p => p.informes === 0) ? (
              <div className="py-12 flex flex-col items-center justify-center opacity-30 text-center">
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">Aún no hay informes registrados en este período</p>
              </div>
            ) : (
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={reportsTrendData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f1f5f9'} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                      formatter={(value: any) => [`${value} informe(s)`, 'Completados']}
                    />
                    <Line type="monotone" dataKey="informes" stroke="#3b82f6" strokeWidth={3} dot={{ r: 3, fill: '#3b82f6' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Monthly Average Timing Chart */}
          <div className={`rounded-[32px] border p-6 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <h3 className="text-xs sm:text-sm font-black uppercase tracking-widest text-indigo-500">
                  Tiempo Promedio
                </h3>
                <div className="relative group inline-flex items-center">
                  <HelpCircle size={14} className="text-slate-400 hover:text-indigo-500 cursor-help transition-colors" />
                  <div className="absolute right-0 sm:left-0 top-full mt-2 hidden group-hover:block z-50 w-64 p-2.5 rounded-xl shadow-xl border text-[11px] font-medium leading-relaxed pointer-events-none bg-slate-900 text-slate-100 border-slate-700">
                    Minutos por estudio desglosados en tiempo de dictado clínico del especialista y tiempo de fusión con IA.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Dictado</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Fusión</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Total</span>
                </div>
              </div>
            </div>

            {monthlyTimingData.length > 0 ? (
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyTimingData}>
                    <defs>
                      <linearGradient id="colorTimingTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorTimingDictation" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorTimingFusion" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f1f5f9'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                      dy={10}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                      unit="m"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                      formatter={(value: any, name: any) => {
                        const formattedVal = `${value} min`;
                        if (name === 'dictado') return [formattedVal, 'Dictado'];
                        if (name === 'fusion') return [formattedVal, 'Fusión'];
                        if (name === 'total') return [formattedVal, 'Tiempo Total'];
                        return [formattedVal, name];
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorTimingTotal)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="dictado" 
                      stroke="#3b82f6" 
                      strokeWidth={2.5}
                      fillOpacity={1} 
                      fill="url(#colorTimingDictation)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="fusion" 
                      stroke="#8b5cf6" 
                      strokeWidth={2.5}
                      fillOpacity={1} 
                      fill="url(#colorTimingFusion)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center opacity-30 text-center">
                <Clock size={36} className="mb-2 text-slate-400" />
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                  Aún no hay estadísticas de tiempo registradas
                </p>
                <p className="text-[10px] text-slate-500 mt-1 max-w-sm">
                  Las estadísticas de tiempo aparecerán a medida que se generen más informes en Modular-1.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Gastos (Mes Actual) */}
        <div className={`rounded-[32px] border p-6 sm:p-8 shadow-sm transition-all mb-8 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                <DollarSign size={20} />
              </div>
              <div>
                <h3 className={`text-base font-black uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Gastos (Mes Actual)
                </h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Ciclo en curso • Cargo base y consumo acumulado de inferencias IA
                </p>
              </div>
            </div>
            {isCustomFee && (
              <span className="self-start sm:self-center px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-800">
                Tarifa personalizada
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-[#1e2024] border-gray-800' : 'bg-slate-50/70 border-slate-100'}`}>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                Cargo Base Mensual
              </span>
              <div className="flex items-baseline gap-1">
                <span className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  ${currentBaseFee.toFixed(2)}
                </span>
                <span className="text-[10px] font-bold text-slate-400">USD</span>
              </div>
              <p className="text-[9px] font-medium text-slate-400 mt-1">
                {isCustomFee ? 'Tarifa preferencial asignada' : 'Tarifa base estándar mensual'}
              </p>
            </div>

            <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-[#1e2024] border-gray-800' : 'bg-slate-50/70 border-slate-100'}`}>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                Consumo API (Tokens)
              </span>
              <div className="flex items-baseline gap-1">
                <span className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  ${monthTokenCost.toFixed(2)}
                </span>
                <span className="text-[10px] font-bold text-slate-400">USD</span>
              </div>
              <p className="text-[9px] font-medium text-slate-400 mt-1">
                Inferencia de modelos de redacción
              </p>
            </div>

            <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-emerald-950/20 border-emerald-800/40' : 'bg-emerald-50/60 border-emerald-100'}`}>
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 block mb-1">
                Total Acumulado Mes
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-emerald-500">
                  ${totalMonthCost.toFixed(2)}
                </span>
                <span className="text-[10px] font-bold text-emerald-600/70 dark:text-emerald-400/70">USD</span>
              </div>
              <p className="text-[9px] font-medium text-emerald-700/80 dark:text-emerald-300/80 mt-1">
                Base + Consumo devengado
              </p>
            </div>
          </div>
        </div>

        {/* Row 2: Stats Grid (Tiempo Ahorrado, Interacciones) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <StatCard 
            title="Tiempo Ahorrado" 
            value={formatHours(stats?.weeklyROI?.[stats.weeklyROI.length - 1]?.hours || 0)} 
            subtitle="Esta semana"
            icon={<Clock className="text-blue-500" />}
            trend="+12%"
            isPositive={true}
            isDarkMode={isDarkMode}
          />
          <StatCard 
            title="Interacciones" 
            value={stats?.roiEvents?.length || 0} 
            subtitle="Total histórico"
            icon={<Zap className="text-amber-500" />}
            trend="+5"
            isPositive={true}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Row 3: Charts Grid (Ahorro de Tiempo, Distribución) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Main Chart */}
          <div className={`lg:col-span-2 rounded-[32px] border p-8 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-blue-500 mb-1">Ahorro de Tiempo</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {timeRange === 'today' ? 'Horas recuperadas por horas' : 
                   timeRange === 'day' ? 'Horas recuperadas por día' : 
                   timeRange === 'month' ? 'Horas recuperadas por mes' : 'Horas recuperadas por año'}
                </p>
                {timeRange === 'day' && (
                  <p className="text-lg font-black uppercase tracking-tighter text-slate-500 mt-1">
                    {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
              <BarChart3 size={20} className="text-slate-300" />
            </div>
            
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={roiData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#374151' : '#f1f5f9'} />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      border: 'none',
                      borderRadius: '16px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#3b82f6" 
                    strokeWidth={4}
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Distribution Chart */}
          <div className={`rounded-[32px] border p-8 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-emerald-500 mb-1">Distribución</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tipo de interacciones</p>
              </div>
              <PieChart size={20} className="text-slate-300" />
            </div>

            <div className="h-[250px] w-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={interactionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={8}
                    dataKey="value"
                  >
                    {interactionData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <span className="block text-2xl font-black">{stats?.roiEvents?.length || 0}</span>
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Total</span>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {interactionData.map((item: any, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{item.name}</span>
                  </div>
                  <span className="text-[10px] font-black">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Communication & Feedback Grid - 2 Levels, 4 Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Row 1: Notices & Personal Messages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:col-span-2">
            {/* Notices */}
            <div className={`rounded-[32px] border p-8 shadow-sm h-full ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-red-500/10 p-2.5 rounded-full">
                  <Bell size={20} className="text-red-500" />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest">Avisos Generales</h3>
              </div>
              {notices.length > 0 ? (
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {notices.map((notice) => (
                    <div 
                      key={notice.id} 
                      className={`p-4 rounded-2xl border-l-4 ${
                        notice.type === 'urgent' ? 'border-red-500 bg-red-500/5' :
                        notice.type === 'maintenance' ? 'border-amber-500 bg-amber-500/5' :
                        'border-blue-500 bg-blue-500/5'
                      }`}
                    >
                      <h4 className="text-xs font-black uppercase tracking-tight mb-1">{notice.title}</h4>
                      <p className="text-[11px] text-slate-500 leading-relaxed">{notice.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-10 flex flex-col items-center justify-center opacity-20">
                  <CheckCircle2 size={32} />
                  <p className="text-[10px] font-black uppercase tracking-widest mt-2">No hay avisos nuevos</p>
                </div>
              )}
            </div>

            {/* Personal Messages */}
            <div className={`rounded-[32px] border p-8 shadow-sm h-full ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-500/10 p-2.5 rounded-full">
                    <Mail size={20} className="text-indigo-500" />
                  </div>
                  <h3 className="text-sm font-black uppercase tracking-widest">Mensajes Personales</h3>
                </div>
                {userMessages.filter(m => !m.read).length > 0 && (
                  <span className="px-2 py-1 bg-indigo-600 text-white text-[8px] font-black uppercase rounded-full animate-pulse">
                    {userMessages.filter(m => !m.read).length} Nuevos
                  </span>
                )}
              </div>
              
              {userMessages.length > 0 ? (
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {userMessages.map((msg) => (
                    <div 
                      key={msg.id} 
                      onClick={() => !msg.read && handleMarkAsRead(msg.id)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer group ${
                        !msg.read 
                          ? (isDarkMode ? 'bg-indigo-900/20 border-indigo-500/50 shadow-md ring-1 ring-indigo-500/20 scale-[1.01]' : 'bg-indigo-50 border-indigo-200 shadow-md ring-1 ring-indigo-100 scale-[1.01]') 
                          : (isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100')
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          {msg.type === 'VALIDATION' ? <ShieldAlert size={14} className="text-amber-500" /> : <Mail size={14} className="text-indigo-500" />}
                          <span className="text-[10px] font-black uppercase tracking-tight text-indigo-500">{msg.senderName}</span>
                        </div>
                        <span className="text-[8px] font-bold text-gray-400 uppercase">{new Date(msg.timestamp).toLocaleDateString()}</span>
                      </div>
                      <p className={`text-[11px] leading-relaxed mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} ${!msg.read ? 'font-bold' : ''}`}>
                        {msg.text}
                      </p>
                      {!msg.read && (
                        <div className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-indigo-500 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <CheckCircle2 size={10} /> Click para leer
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-10 flex flex-col items-center justify-center opacity-20">
                  <Mail size={32} />
                  <p className="text-[10px] font-black uppercase tracking-widest mt-2">Bandeja de entrada vacía</p>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Recent Chats & Suggestions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:col-span-2">
            {/* Recent Chats */}
            <div className={`rounded-[32px] border p-8 shadow-sm h-full ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-emerald-500/10 p-2.5 rounded-full">
                  <MessageSquare size={20} className="text-emerald-500" />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest">Chats Recientes</h3>
              </div>
              
              {activeChats.length > 0 ? (
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {activeChats.slice(0, 5).map((chat) => {
                    const otherId = chat.participants.find(p => p !== auth.currentUser?.uid);
                    const otherUser = directory.find(u => u.uid === otherId);
                    return (
                      <button 
                        key={chat.id} 
                        onClick={() => handleOpenChat(chat)}
                        className={`w-full p-4 rounded-2xl border text-left transition-all flex items-center gap-4 ${isDarkMode ? 'bg-gray-800/50 border-gray-700 hover:bg-gray-800' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}
                      >
                        <div className="relative shrink-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs ${isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>
                            {otherUser?.photoURL ? <img src={otherUser.photoURL} className="w-full h-full rounded-xl object-cover" alt="" /> : (otherUser?.displayName || 'U').substring(0,2).toUpperCase()}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-black uppercase tracking-tight truncate">{otherUser?.displayName || 'Usuario'}</p>
                          <p className="text-[10px] text-gray-500 truncate">{chat.lastMessage?.text || 'Sin mensajes'}</p>
                        </div>
                        <span className="text-[8px] font-bold text-gray-400 uppercase shrink-0">{new Date(chat.updatedAt).toLocaleDateString()}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-10 flex flex-col items-center justify-center opacity-20">
                  <MessageSquare size={32} />
                  <p className="text-[10px] font-black uppercase tracking-widest mt-2">No hay chats activos</p>
                </div>
              )}
            </div>

            {/* Suggestions */}
            <div className={`rounded-[32px] border p-8 shadow-sm h-full ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-blue-500/10 p-2.5 rounded-full">
                  <Languages size={20} className="text-blue-500" />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest">Sugerencias</h3>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
                Tus ideas ayudan a mejorar MODULAR. Envíanos tus comentarios.
              </p>
              <div className="space-y-4">
                <textarea
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  placeholder="Escribe aquí tu sugerencia o reporte de error..."
                  className={`w-full h-24 p-4 rounded-2xl border text-sm resize-none focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'}`}
                />
                <button
                  onClick={handleSendSuggestion}
                  disabled={isSendingSuggestion || !suggestion.trim()}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center space-x-2 transition-all"
                >
                  {isSendingSuggestion ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={14} />
                      <span>Enviar Sugerencia</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Activity & Ratings Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Activity Feed */}
          <div className={`rounded-[32px] border p-8 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-500/10 p-2 rounded-xl">
                  <Calendar size={20} className="text-indigo-500" />
                </div>
                <h3 className="text-sm font-black uppercase tracking-widest">Actividad Reciente</h3>
              </div>
            </div>

            <div className="space-y-4">
              {stats?.roiEvents?.slice(0, 5).map((event: any, index: number) => (
                <div key={index} className={`flex items-center justify-between p-4 rounded-2xl border ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-xl ${
                      event.type === ROIEventType.GENERATION ? 'bg-blue-500/10 text-blue-500' :
                      event.type === ROIEventType.TRANSLATION ? 'bg-emerald-500/10 text-emerald-500' :
                      event.type === ROIEventType.PHD_ASSISTANCE ? 'bg-orange-500/10 text-orange-500' :
                      'bg-amber-500/10 text-amber-500'
                    }`}>
                      {event.type === ROIEventType.GENERATION ? <FileText size={16} /> :
                       event.type === ROIEventType.TRANSLATION ? <Languages size={16} /> :
                       event.type === ROIEventType.PHD_ASSISTANCE ? <Brain size={16} /> :
                       <Zap size={16} />}
                    </div>
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-tight">
                        {event.type === ROIEventType.GENERATION ? 'Generación de Informe' :
                         event.type === ROIEventType.TRANSLATION ? 'Traducción Médica' :
                         event.type === ROIEventType.IMPROVEMENT ? 'Mejora de Estilo' :
                         event.type === ROIEventType.AUDIT ? 'Auditoría PhD' : 
                         event.type === ROIEventType.PHD_ASSISTANCE ? 'Asistencia PHD' : 'Estructuración'}
                      </p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[11px] font-black text-emerald-500">+{event.hoursSaved * 60} min</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Module Ratings */}
          <div className={`rounded-[32px] border p-8 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-amber-500/10 p-2 rounded-xl">
                <Star size={20} className="text-amber-500" />
              </div>
              <h3 className="text-sm font-black uppercase tracking-widest">Califica los Módulos</h3>
            </div>
            <div className="space-y-4">
              {[
                { id: 'reports', name: 'Biblioteca' },
                { id: 'phd_assistance', name: 'Asistencia PHD' },
                { id: 'ai_drafting', name: 'Redacción PhD' },
                { id: 'transcription', name: 'Transcripción IA' },
                { id: 'modular_1', name: 'MODULAR-1' },
                { id: 'modular_2', name: 'Agente' },
                { id: 'chat_ia', name: 'Búsqueda IA' },
                { id: 'community', name: 'Comunidad' },
                { id: 'management', name: 'Gestión Master' }
              ].map((module) => (
                <div key={module.id} className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{module.name}</span>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleRateModule(module.id, star)}
                        className={`p-1 transition-all hover:scale-110 ${
                          (ratings[module.id] || 0) >= star ? 'text-amber-500' : 'text-slate-300'
                        }`}
                      >
                        <Star size={16} fill={(ratings[module.id] || 0) >= star ? 'currentColor' : 'none'} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, icon, trend, isPositive, isDarkMode }: any) => (
  <div className={`p-6 rounded-[28px] border shadow-sm transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
    <div className="flex justify-between items-start mb-4">
      <div className={`p-2.5 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        {icon}
      </div>
      <div className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-[9px] font-black ${isPositive ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
        {isPositive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
        <span>{trend}</span>
      </div>
    </div>
    <div>
      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</h4>
      <div className="flex items-baseline space-x-2">
        <span className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</span>
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{subtitle}</span>
      </div>
    </div>
  </div>
);

export default DashboardUserScreen;
